/*
 * FilterGroupForStatus is a filter group component for filtering content by status.
 * It provides a multi-select interface for choosing task/pin statuses, with support for:
 *
 * - Selecting multiple statuses
 * - Disabled state when task pins are not shown
 * - Custom styling through CSS props
 *
 * The component integrates with the FilterSettings system and uses FilterGroupMultiSelect for its UI implementation.
 */
import { FilterSettings } from '@range.io/basic-types'
import React from 'react'
import { FlexColumn } from '../../components-reusable/index.js'
import { styled } from '../../range-theme/index.js'
import { Row } from './filter-primitives/FilterGroupShared.js'

// each status can be toggled on/off
const statusRow = ({ status, disabled = false, showStatuses, filter, filterSettings, setFilterSettings }) => {
    const setChecked = b => setFilterSettings(filter.setStatus(filterSettings, status, b))
    const checked = showStatuses.includes(status) && !disabled
    const props = { title: status.name, checked, disabled, setChecked }
    return <Row key={status.id} {...props} />
}

const StyledStatusGroup = styled(FlexColumn, {
    w: 'auto',
    boxSizing: 'border-box',
    ml: 16,
    mr: 16,
    gap: 12,
    pl: 0,
    pr: 0,
    pt: 16,
    pb: 16,
    borderTop: '1px solid $neutral07',
    '&:first-of-type': {
        borderTop: 'none',
    },
})

/*
 * FilterGroupForStatus component for filtering by status
 * @sig FilterGroupForStatus :: ({[Status], Boolean, FilterSettings, (FilterSettings -> ())}, CSS) -> ReactElement
 */
const FilterGroupForStatus = ({
    allStatuses,
    disabled = false,
    filterSettings,
    setFilterSettings,
    css = {},
    'data-cy': dataCy,
}) => {
    const { showStatuses } = filterSettings

    return (
        <StyledStatusGroup style={css} data-cy={dataCy}>
            {allStatuses.map(status =>
                statusRow({
                    status,
                    disabled,
                    showStatuses,
                    filter: FilterSettings,
                    filterSettings,
                    setFilterSettings,
                })
            )}
        </StyledStatusGroup>
    )
}

export default FilterGroupForStatus
